// frontend/src/pages/ClientRequestForm.tsx
// Версия 3.1.0 — локальный выбор timezone для начала теста

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Collapse,
  IconButton,
  MenuItem,
} from '@mui/material';
import {
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiUrl } from '../config/paths';
import { toUtcIso, SstoTimezone } from '../utils/sstoTimezone';

function authFetch(input: RequestInfo, init: RequestInit = {}) {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  const headers = {
    'Content-Type': 'application/json',
    ...(init.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(input, { ...init, headers });
}

function getTerminalType(value: string): 'INMARSAT_C' | 'IRIDIUM' | 'UNKNOWN' {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 9) {
    return 'INMARSAT_C';
  }
  if (cleaned.length === 15) return 'IRIDIUM';
  return 'UNKNOWN';
}

type RequestPrefill = Record<string, unknown>;

const timezoneOptions: Array<{ value: SstoTimezone; label: string; description: string }> = [
  {
    value: 'local',
    label: 'Местное время',
    description: 'часовой пояс браузера',
  },
  {
    value: 'utc',
    label: 'UTC',
    description: 'всемирное координированное время',
  },
  {
    value: 'moscow',
    label: 'Москва',
    description: 'Europe/Moscow, UTC+3',
  },
];

function getTimezoneOptionLabel(value: SstoTimezone): string {
  return timezoneOptions.find((option) => option.value === value)?.label ?? 'Местное время';
}

function formatUtcDateTime(value: string | null): string {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return (
    new Intl.DateTimeFormat('ru-RU', {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .format(date)
      .replace(',', '') + ' UTC'
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function getRequestPrefill(state: unknown): RequestPrefill | null {
  if (!isRecord(state) || !isRecord(state.prefill)) return null;
  return state.prefill;
}

function firstText(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number') return String(value);
  }
  return '';
}

function formatAdditionalEmails(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((item) => firstText(item))
      .filter(Boolean)
      .join(', ');
  }
  return firstText(value);
}

const ClientRequestForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const requestPrefill = getRequestPrefill(location.state);
  const resendFromRequestId = isRecord(location.state)
    ? firstText(location.state.resendFromRequestId)
    : '';
  const prefillAdditionalEmails = formatAdditionalEmails(
    requestPrefill?.additional_emails ?? requestPrefill?.additionalEmails
  );
  const prefillContactEmail = firstText(
    requestPrefill?.contact_email,
    requestPrefill?.contactEmail
  );
  const prefillContactPhone = firstText(
    requestPrefill?.contact_phone,
    requestPrefill?.contactPhone,
    requestPrefill?.phone
  );
  const [loading, setLoading] = useState(false);
  const [showOptional, setShowOptional] = useState(() =>
    Boolean(firstText(requestPrefill?.mmsi) || prefillAdditionalEmails)
  );

  const [terminalNumber, setTerminalNumber] = useState(() =>
    firstText(requestPrefill?.ssas_number, requestPrefill?.terminal_number)
  );
  const [testStart, setTestStart] = useState('');
  const [timezoneMode, setTimezoneMode] = useState<SstoTimezone>('local');
  const [contactEmail, setContactEmail] = useState(() => prefillContactEmail);

  const [vesselName, setVesselName] = useState(() =>
    firstText(requestPrefill?.vessel_name, requestPrefill?.vesselName, requestPrefill?.name)
  );
  const [mmsi, setMmsi] = useState(() => firstText(requestPrefill?.mmsi));
  const [imoNumber, setImoNumber] = useState(() =>
    firstText(requestPrefill?.imo_number, requestPrefill?.imo)
  );
  const [ownerOrganization, setOwnerOrganization] = useState(() =>
    firstText(requestPrefill?.owner_organization, requestPrefill?.ship_owner)
  );
  const [ownerAddress, setOwnerAddress] = useState('');
  const [contactPhone, setContactPhone] = useState(() => prefillContactPhone);
  const [portOfRegistry, setPortOfRegistry] = useState('');
  const [additionalEmails, setAdditionalEmails] = useState(() => prefillAdditionalEmails);
  const [notes, setNotes] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const endpoints = [apiUrl('requests/web'), apiUrl('requests')];

  useEffect(() => {
    if (prefillContactEmail) return;
    const storedEmail = sessionStorage.getItem('email');
    if (storedEmail) setContactEmail(storedEmail);
  }, [prefillContactEmail]);

  const validate = (): string[] => {
    const errors: string[] = [];
    const cleanedTerminal = terminalNumber.replace(/\D/g, '');

    if (!cleanedTerminal) errors.push('Номер терминала ССТО обязателен');
    if (!testStart.trim()) errors.push('Начало теста обязательно');
    if (!contactEmail.trim()) errors.push('Контактный email обязателен');
    if (!vesselName.trim()) errors.push('Название судна обязательно');
    if (!imoNumber.trim()) errors.push('ИМО номер обязателен');
    if (!contactPhone.trim()) errors.push('Контактный телефон обязателен');
    if (!ownerOrganization.trim()) errors.push('Судовладелец обязателен');

    if (cleanedTerminal && !/^(\d{9}|\d{15})$/.test(cleanedTerminal)) {
      errors.push('Номер терминала: 9 цифр (Inmarsat-C) или 15 цифр (Iridium)');
    }

    if (imoNumber.trim() && !/^\d{7}$/.test(imoNumber.trim())) {
      errors.push('ИМО номер должен содержать ровно 7 цифр');
    }

    if (mmsi.trim() && !/^\d{9}$/.test(mmsi.trim())) {
      errors.push('MMSI должен содержать ровно 9 цифр');
    }

    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      errors.push('Некорректный формат email');
    }

    if (contactPhone.trim() && contactPhone.replace(/\D/g, '').length < 7) {
      errors.push('Телефон должен содержать минимум 7 цифр');
    }

    // Валидация ±24ч — через toUtcIso для корректного timezone
    if (testStart.trim()) {
      const startUtcStr = toUtcIso(testStart.trim(), timezoneMode);
      if (startUtcStr) {
        const d = new Date(startUtcStr);
        const now = new Date();
        const past24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const future24h = new Date(now.getTime() + 24 * 60 * 60 * 1000);

        if (d < past24h) {
          errors.push('Дата начала теста не может быть ранее чем 24 часа назад');
        }
        if (d > future24h) {
          errors.push('Дата начала теста не может быть позднее чем через 24 часа');
        }
      }
    }

    // testEnd validation removed — auto-calculated as start + 24h

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validate();
    if (errors.length > 0) {
      setError(errors.join('. '));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cleanedTerminal = terminalNumber.replace(/\D/g, '').trim();
      const startUtc = testStart.trim() ? toUtcIso(testStart.trim(), timezoneMode) : undefined;
      const endUtc = startUtc
        ? new Date(new Date(startUtc).getTime() + 24 * 60 * 60 * 1000).toISOString()
        : undefined;

      const payload: any = {
        terminal_number: cleanedTerminal,
        ssas_number: cleanedTerminal,
        planned_test_date: startUtc,
        test_date: startUtc,
        start_time: startUtc,
        end_time: endUtc,
        contact_email: contactEmail.trim(),
        vessel_name: vesselName.trim(),
        mmsi: mmsi.trim() || undefined,
        imo_number: imoNumber.trim(),
        port_of_registry: portOfRegistry.trim() || undefined,
        owner_organization: ownerOrganization.trim(),
        owner_address: ownerAddress.trim() || undefined,
        contact_phone: contactPhone.trim(),
        additional_emails: additionalEmails.trim() || undefined,
        notes: notes.trim() || undefined,
        source: 'WEB_FORM',
      };

      let response = await authFetch(endpoints[0], {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      // Fallback: если /requests/web недоступен — попробовать /requests
      if (!response.ok && endpoints.length > 1) {
        const firstError = await response.json().catch(() => ({}));
        // 4xx = валидация, не пробуем fallback; 5xx/network = пробуем
        if (response.status >= 500 || response.status === 0) {
          response = await authFetch(endpoints[1], {
            method: 'POST',
            body: JSON.stringify(payload),
          });
        } else {
          throw new Error(firstError.message || 'Ошибка при создании заявки');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Ошибка при создании заявки');
      }

      await response.json();
      setSuccess('Заявка успешно создана!');

      setTimeout(() => {
        navigate('/client/requests');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Ошибка при отправке заявки');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      if (loading) return;

      const errors = validate();
      if (errors.length > 0) {
        setError(errors.join('. '));
        return;
      }

      handleSubmit();
    },
    [
      loading,
      terminalNumber,
      testStart,
      timezoneMode,
      contactEmail,
      vesselName,
      mmsi,
      imoNumber,
      portOfRegistry,
      ownerOrganization,
      ownerAddress,
      contactPhone,
      additionalEmails,
      notes,
    ]
  );

  const selectedTimezoneLabel = getTimezoneOptionLabel(timezoneMode);
  const startUtcPreview = testStart.trim() ? toUtcIso(testStart.trim(), timezoneMode) : null;
  const endUtcPreview = startUtcPreview
    ? new Date(new Date(startUtcPreview).getTime() + 24 * 60 * 60 * 1000).toISOString()
    : null;
  const utcIntervalLabel =
    startUtcPreview && endUtcPreview
      ? `${formatUtcDateTime(startUtcPreview)} → ${formatUtcDateTime(endUtcPreview)}`
      : 'укажите начало теста';

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Новая заявка на тестирование ССТО
        </Typography>

        {resendFromRequestId && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Создание новой заявки на основе предыдущей
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Обязательные поля
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              onKeyDown={handleKeyDown}
              fullWidth
              required
              label="Номер терминала ССТО"
              value={terminalNumber}
              onChange={(e) => setTerminalNumber(e.target.value)}
              placeholder="9 цифр (Inmarsat-C) или 15 цифр (Iridium)"
              error={terminalNumber.length > 0 && getTerminalType(terminalNumber) === 'UNKNOWN'}
              helperText={
                terminalNumber.length > 0 && getTerminalType(terminalNumber) === 'UNKNOWN'
                  ? '9 цифр (Inmarsat-C) или 15 цифр (Iridium)'
                  : terminalNumber
                    ? `Тип терминала: ${getTerminalType(terminalNumber) === 'INMARSAT_C' ? 'Inmarsat-C' : 'Iridium'}`
                    : 'Введите номер терминала'
              }
            />
          </Grid>

          {/* ===== Начало теста + Окончание теста ===== */}
          <Grid item xs={12} md={7}>
            <TextField
              onKeyDown={handleKeyDown}
              fullWidth
              required
              type="datetime-local"
              label="Начало теста"
              value={testStart}
              onChange={(e) => setTestStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
                max: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
              }}
              helperText="Укажите дату/время начала (±24ч). Окончание теста (+24ч) рассчитается автоматически."
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              select
              label="Часовой пояс"
              value={timezoneMode}
              onChange={(e) => setTimezoneMode(e.target.value as SstoTimezone)}
              helperText="Выберите, как трактовать введённое время"
            >
              {timezoneOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="body2"
              component="p"
              sx={{
                mt: -0.5,
                color: '#d32f2f',
                fontWeight: 700,
                lineHeight: 1.35,
                textDecoration: 'underline',
              }}
            >
              Внимание! Для корректного подтверждения получения сигнала, дата срабатывания сигнала
              должна быть строго в окне тестирования, в противном случае заявка может быть отклонена
              оперативным дежурным.
            </Typography>
          </Grid>
          {/* Окончание теста = начало + 24ч (скрыто от пользователя) */}
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Время вводится в выбранном часовом поясе: {selectedTimezoneLabel}. В заявке будет
              сохранён UTC-интервал.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              UTC-интервал теста: {utcIntervalLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Москва используется как UTC+3 без сезонного перевода времени.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              onKeyDown={handleKeyDown}
              fullWidth
              required
              type="email"
              label="Контактный email для отправки результатов"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              helperText="Заявка будет привязана к вашему кабинету автоматически. Здесь можно указать другой email для получения результатов"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              onKeyDown={handleKeyDown}
              fullWidth
              required
              label="Название судна"
              value={vesselName}
              onChange={(e) => setVesselName(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              onKeyDown={handleKeyDown}
              fullWidth
              required
              label="ИМО номер"
              value={imoNumber}
              onChange={(e) => setImoNumber(e.target.value)}
              placeholder="7 цифр"
              error={imoNumber.length > 0 && !/^\d{7}$/.test(imoNumber)}
              helperText={
                imoNumber.length > 0 && !/^\d{7}$/.test(imoNumber) ? 'ИМО: ровно 7 цифр' : ''
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              onKeyDown={handleKeyDown}
              fullWidth
              required
              label="Контактный телефон"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+7 (XXX) XXX-XX-XX"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              onKeyDown={handleKeyDown}
              fullWidth
              required
              label="Судовладелец"
              value={ownerOrganization}
              onChange={(e) => setOwnerOrganization(e.target.value)}
              placeholder="ООО «Название компании»"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Дополнительная информация (опционально)</Typography>
          <IconButton onClick={() => setShowOptional(!showOptional)}>
            {showOptional ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={showOptional}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                onKeyDown={handleKeyDown}
                fullWidth
                label="MMSI"
                value={mmsi}
                onChange={(e) => setMmsi(e.target.value)}
                placeholder="9 цифр"
                error={mmsi.length > 0 && !/^\d{9}$/.test(mmsi)}
                helperText={mmsi.length > 0 && !/^\d{9}$/.test(mmsi) ? 'MMSI: ровно 9 цифр' : ''}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                onKeyDown={handleKeyDown}
                fullWidth
                label="Порт приписки"
                value={portOfRegistry}
                onChange={(e) => setPortOfRegistry(e.target.value)}
                placeholder="Например: Владивосток"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                onKeyDown={handleKeyDown}
                fullWidth
                label="Адрес судовладельца"
                value={ownerAddress}
                onChange={(e) => setOwnerAddress(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                onKeyDown={handleKeyDown}
                fullWidth
                label="Дополнительные email"
                value={additionalEmails}
                onChange={(e) => setAdditionalEmails(e.target.value)}
                helperText="До 3 адресов, через запятую или точку с запятой"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Примечания"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
          </Grid>
        </Collapse>

        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/client')} disabled={loading}>
            Отмена
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {loading ? 'Отправка...' : 'Отправить заявку'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ClientRequestForm;
